/**
 * Ad card themes for the admin ColorPicker.
 *
 * Multi-tenant model:
 *  - `DEFAULT_AD_THEMES` is the neutral "Ads theming" palette from the design
 *    (Default + Seasons, Light and Dark). It has no brand hue, so it works for
 *    every tenant.
 *  - `AD_THEMES_BY_BRAND` lets a brand key from `brands.ts` replace or extend
 *    that palette without touching other tenants.
 *  - `tenant.settings.adThemes` (backend) overrides both at runtime, so a new
 *    tenant can ship its own palette without a deploy.
 *  - Every tenant also gets a "Brand · Light" tint derived from its primary
 *    color (`--tenant-primary`), which is always readable with black text.
 *
 * Hex values of the default palette are approximated from the design mockup —
 * confirm against Figma before treating them as canonical. Spring · Light
 * matches the mobile app's `bannerColor` (#F6E4CC), which is also the API
 * default.
 *
 * The mobile app paints ad title/description in fixed black regardless of the
 * background (SAM-432), so Dark themes are unreadable until that ships. They
 * stay listed so editors see the full system, but disabled.
 */
export type AdThemeMode = 'light' | 'dark';

export interface AdTheme {
  id: string;
  name: string;
  hex: string;
  mode: AdThemeMode;
}

/** Flip to true once SAM-432 (theme-aware text color) is in production. */
export const DARK_AD_THEMES_ENABLED = false;

/** Text color the app renders on ad cards today. */
export const AD_TEXT_COLOR = '#000000';

/** WCAG AA for normal text. */
export const MIN_AD_CONTRAST = 4.5;

export const DEFAULT_AD_THEME_HEX = '#F6E4CC';

export const DEFAULT_AD_THEMES: AdTheme[] = [
  { id: 'default-light', name: 'Default · Light', hex: '#FFFFFF', mode: 'light' },
  { id: 'spring-light', name: 'Spring · Light', hex: '#F6E4CC', mode: 'light' },
  { id: 'summer-light', name: 'Summer · Light', hex: '#D9F0F1', mode: 'light' },
  { id: 'autumn-light', name: 'Autumn · Light', hex: '#E6F0D2', mode: 'light' },
  { id: 'winter-light', name: 'Winter · Light', hex: '#F7E3F1', mode: 'light' },
  { id: 'default-dark', name: 'Default · Dark', hex: '#000000', mode: 'dark' },
  { id: 'spring-dark', name: 'Spring · Dark', hex: '#3B2E00', mode: 'dark' },
  { id: 'summer-dark', name: 'Summer · Dark', hex: '#0E4A57', mode: 'dark' },
  { id: 'autumn-dark', name: 'Autumn · Dark', hex: '#4B1739', mode: 'dark' },
  { id: 'winter-dark', name: 'Winter · Dark', hex: '#252163', mode: 'dark' },
];

/**
 * Per-brand palettes keyed by `BrandConfig.key`. A brand listed here replaces
 * the default palette entirely; omit a brand to keep the default. Add entries
 * as tenants get their own Ads theming in Figma.
 */
export const AD_THEMES_BY_BRAND: Record<string, AdTheme[]> = {
  // capcana: DEFAULT_AD_THEMES — the design was made for Cap Cana; no override.
  // cityplace / zabelabeach: pending their own palette, they use the default.
};

const parseHex = (hex: string): [number, number, number] | null => {
  const clean = hex.trim().replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const toHex = (r: number, g: number, b: number) =>
  '#' + [r, g, b].map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('').toUpperCase();

const channel = (v: number) => {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};

/** WCAG 2.x relative luminance, or null for an invalid hex. */
export const relativeLuminance = (hex: string): number | null => {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb;
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

/** WCAG contrast ratio between two colors (1..21), or null if either hex is invalid. */
export const contrastRatio = (a: string, b: string): number | null => {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la === null || lb === null) return null;
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
};

/** Weekly Wave 22 oct (SAM-1105): a white or almost white card looks washed out in the app. */
export const NEAR_WHITE_MAX_CONTRAST = 1.15;

/** True for white and for colours too close to it (WCAG contrast against white under 1.15, about #EFEFEF and lighter). */
export const isNearWhite = (hex: string): boolean => {
  const ratio = contrastRatio(hex, '#FFFFFF');
  return ratio !== null && ratio < NEAR_WHITE_MAX_CONTRAST;
};

/**
 * Light tint of a brand color: 15% brand over white. Any hue at that mix
 * keeps black text above 4.5:1, so every tenant gets one safe branded theme.
 */
export const brandTintTheme = (primaryHex: string): AdTheme | null => {
  const rgb = parseHex(primaryHex);
  if (!rgb) return null;
  const mix = (c: number) => c * 0.15 + 255 * 0.85;
  return { id: 'brand-light', name: 'Brand · Light', hex: toHex(mix(rgb[0]), mix(rgb[1]), mix(rgb[2])), mode: 'light' };
};

const isAdTheme = (t: unknown): t is AdTheme =>
  !!t && typeof t === 'object' &&
  typeof (t as AdTheme).id === 'string' &&
  typeof (t as AdTheme).name === 'string' &&
  typeof (t as AdTheme).hex === 'string' && parseHex((t as AdTheme).hex) !== null &&
  ((t as AdTheme).mode === 'light' || (t as AdTheme).mode === 'dark');

export interface ResolveAdThemesInput {
  /** `BrandConfig.key` for the current host, if any. */
  brandKey?: string | null;
  /** Tenant primary color (the value behind `--tenant-primary`). */
  tenantPrimary?: string | null;
  /** `tenant.settings.adThemes` from the backend, if the tenant defines one. */
  tenantOverride?: unknown;
}

/** Palette for the current tenant: backend override > brand override > default, plus the brand tint. */
export const resolveAdThemes = ({ brandKey, tenantPrimary, tenantOverride }: ResolveAdThemesInput): AdTheme[] => {
  let base: AdTheme[] | null = null;
  if (Array.isArray(tenantOverride) && tenantOverride.length > 0 && tenantOverride.every(isAdTheme)) {
    base = tenantOverride;
  } else if (brandKey && AD_THEMES_BY_BRAND[brandKey]) {
    base = AD_THEMES_BY_BRAND[brandKey];
  }
  const themes = base ?? DEFAULT_AD_THEMES;
  const tint = tenantPrimary ? brandTintTheme(tenantPrimary) : null;
  if (!tint || themes.some((t) => t.id === tint.id)) return themes;
  // Brand tint goes first: it's the one theme guaranteed to match the tenant.
  return [tint, ...themes];
};
